import React from 'react';
import { Card, Table, Typography, Alert, Descriptions, Button } from 'antd';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import type { AutofacturaOrderSummary } from '../../types/autofactura';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';

const { Text } = Typography;

interface OrderSummaryStepProps {
  order: AutofacturaOrderSummary;
  onContinue: () => void;
  onCancel: () => void;
}

const formaPagoLabels: Record<string, string> = {
  '01': 'Efectivo',
  '02': 'Cheque nominativo',
  '03': 'Transferencia electrónica',
  '04': 'Tarjeta de crédito',
  '28': 'Tarjeta de débito',
  '99': 'Por definir',
};

const OrderSummaryStep: React.FC<OrderSummaryStepProps> = ({ order, onContinue, onCancel }) => {
  const { formatInTimeZone } = useRestaurantTimeZone();
  const columns = [
    {
      title: 'Producto',
      dataIndex: 'producto',
      key: 'producto',
    },
    {
      title: 'Cant.',
      dataIndex: 'cantidad',
      key: 'cantidad',
      width: 60,
      align: 'center' as const,
    },
    {
      title: 'P. Unit.',
      dataIndex: 'precioUnitario',
      key: 'precioUnitario',
      width: 100,
      align: 'right' as const,
      render: (val: number) => `$${val.toFixed(2)}`,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 100,
      align: 'right' as const,
      render: (val: number) => `$${val.toFixed(2)}`,
    },
  ];

  const esCuenta = order.cuentaNumber != null;

  if (order.yaFacturada) {
    return (
      <div>
        <Alert
          type="warning"
          message={esCuenta ? 'Cuenta ya facturada' : 'Orden ya facturada'}
          description={
            order.facturaEstado === 'CancelacionEnProceso'
              ? 'La factura anterior de esta orden está en proceso de cancelación ante el SAT. Intenta más tarde.'
              : `${esCuenta ? 'Esta cuenta' : 'Esta orden'} ya fue facturada.`
          }
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
        />
        <Button block size="large" onClick={onCancel}>
          Facturar otra orden
        </Button>
      </div>
    );
  }

  if (!order.dentroDelPlazo) {
    return (
      <div>
        <Alert
          type="error"
          message="Fuera de plazo"
          description="Solo se pueden facturar órdenes del mes actual. El plazo para facturar esta orden ha vencido."
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Button block size="large" onClick={onCancel}>
          Facturar otra orden
        </Button>
      </div>
    );
  }

  return (
    <div>
      {order.enFacturaGlobal && (
        <Alert
          type="info"
          message="Este ticket está en la factura global del restaurante"
          description="Al pedir tu factura, el restaurante la emite a tu nombre y reexpide su factura global sin este ticket."
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {esCuenta && (
        <Alert
          type="info"
          message={`Estás facturando la ${order.cuentaName || `cuenta ${order.cuentaNumber}`} de esta orden`}
          description="La orden se cobró con cuenta dividida; cada cuenta se factura por separado."
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Orden #">{order.orderNumber}</Descriptions.Item>
        {esCuenta && (
          <Descriptions.Item label="Cuenta">
            {order.cuentaName || `Cuenta ${order.cuentaNumber}`}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Fecha">
          {formatInTimeZone(order.fecha, 'DD/MM/YYYY HH:mm')}
        </Descriptions.Item>
        {order.formaPago && (
          <Descriptions.Item label="Método de pago">
            {formaPagoLabels[order.formaPago] || order.formaPago}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Table
        dataSource={order.items}
        columns={columns}
        pagination={false}
        size="small"
        rowKey={(_, index) => String(index)}
        style={{ marginBottom: 16 }}
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <Text>Subtotal:</Text>
          <Text>${order.subtotal.toFixed(2)}</Text>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <Text>IVA:</Text>
          <Text>${order.impuestos.toFixed(2)}</Text>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Text strong style={{ fontSize: 16 }}>Total:</Text>
          <Text strong style={{ fontSize: 16 }}>${order.total.toFixed(2)}</Text>
        </div>
      </Card>

      <Button
        type="primary"
        block
        size="large"
        icon={<CheckCircleOutlined />}
        onClick={onContinue}
        style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)', marginBottom: 8 }}
      >
        Continuar a datos fiscales
      </Button>
      <Button block size="large" onClick={onCancel}>
        Cancelar
      </Button>
    </div>
  );
};

export default OrderSummaryStep;
